import { Group, Progress, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconCloudUpload, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { ALLOWED_FILE_EXTENSIONS, fileExtension, formatBytes, maxUploadBytes, type FileArea, type FileRefDto } from '@mexp/shared';
import { errorMessage } from '@/api/client';
import { notifyError } from '@/lib/notify';
import { FileList } from './FileList';
import { uploadFile } from './files';

interface Uploading {
  key: string;
  name: string;
  progress: number;
  error?: string;
}

/**
 * Drag-and-drop uploader. Files go straight to storage as they are dropped; `value` holds the finished
 * uploads, which the parent attaches to a record by id when it saves.
 */
export function FileUploader({
  area,
  value,
  onChange,
  context,
  label = 'Drop files here or click to choose',
  hint,
  multiple = true,
  disabled,
  onBusyChange,
}: {
  area: FileArea;
  value: FileRefDto[];
  onChange: (files: FileRefDto[]) => void;
  context?: Record<string, string>;
  label?: string;
  hint?: string;
  multiple?: boolean;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [uploading, setUploading] = useState<Uploading[]>([]);
  const max = maxUploadBytes(area);

  const handle = async (files: File[]) => {
    const accepted: File[] = [];
    for (const file of files) {
      if (!(ALLOWED_FILE_EXTENSIONS as readonly string[]).includes(fileExtension(file.name))) {
        notifyError(new Error(`${file.name}: this file type is not allowed`), 'File not added');
      } else if (file.size > max) {
        notifyError(new Error(`${file.name} is larger than ${formatBytes(max)}`), 'File not added');
      } else if (file.size === 0) {
        notifyError(new Error(`${file.name} is empty`), 'File not added');
      } else accepted.push(file);
    }
    if (!accepted.length) return;
    onBusyChange?.(true);
    let current = value;
    await Promise.all(
      accepted.map(async (file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`;
        setUploading((u) => [...u, { key, name: file.name, progress: 0 }]);
        try {
          const stored = await uploadFile(file, area, {
            context,
            onProgress: (progress) => setUploading((u) => u.map((x) => (x.key === key ? { ...x, progress } : x))),
          });
          current = multiple ? [...current, stored] : [stored];
          onChange(current);
          setUploading((u) => u.filter((x) => x.key !== key));
        } catch (error) {
          setUploading((u) => u.map((x) => (x.key === key ? { ...x, error: errorMessage(error) } : x)));
        }
      }),
    );
    onBusyChange?.(false);
  };

  return (
    <Stack gap="sm">
      <Dropzone onDrop={(files) => void handle(files)} multiple={multiple} disabled={disabled} maxSize={max} p="md" radius="md">
        <Group justify="center" gap="md" mih={64} style={{ pointerEvents: 'none' }} wrap="nowrap">
          <Dropzone.Accept>
            <IconCloudUpload size={32} color="var(--mantine-color-brand-6)" />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={32} color="var(--mantine-color-red-6)" />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconCloudUpload size={32} color="var(--mantine-color-dimmed)" stroke={1.5} />
          </Dropzone.Idle>
          <Stack gap={2}>
            <Text fz="sm" fw={500}>
              {label}
            </Text>
            <Text fz="xs" c="dimmed">
              {hint ?? `PDF, Office, images or ZIP · up to ${formatBytes(max)} each`}
            </Text>
          </Stack>
        </Group>
      </Dropzone>
      {uploading.map((u) => (
        <Stack key={u.key} gap={4}>
          <Group justify="space-between">
            <Text fz="sm" truncate="end" maw="70%">
              {u.name}
            </Text>
            <Text fz="xs" c={u.error ? 'red' : 'dimmed'}>
              {u.error ?? `${u.progress}%`}
            </Text>
          </Group>
          {!u.error && <Progress value={u.progress} size="sm" animated={u.progress < 100} />}
          {u.error && (
            <Text fz="xs" c="dimmed" style={{ cursor: 'pointer' }} onClick={() => setUploading((x) => x.filter((y) => y.key !== u.key))}>
              Dismiss
            </Text>
          )}
        </Stack>
      ))}
      {value.length > 0 && <FileList files={value} onRemove={(id) => onChange(value.filter((f) => f.id !== id))} />}
    </Stack>
  );
}
